import {
  PromoGrantCreateSchema,
  type PromoGrantCreateRequest,
  type UserEntitlement,
} from '@aysar/contracts';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { type FieldDef } from '@/shared/components/dynamic-form/types/dynamic-form.types';
import { useBrand } from '@/shared/hooks/useBrand/useBrand';
import { useDirection } from '@/shared/hooks/useDirection/useDirection';
import { useHasPermission } from '@/shared/hooks/useHasPermission/useHasPermission';
import { useToast } from '@/shared/hooks/useToast/useToast';
import { useResolveUserIds } from '@/shared/services/user-lookup/user-lookup.api';
import { formatDateTime } from '@/shared/utils/format-datetime/format-datetime.util';
import { toApiError } from '@/shared/utils/transform-error/transform-error';

import { buildPromoGrantFields } from '../../config/promo-grant-form.config';
import {
  useGrantPromoMutation,
  useMemberEntitlementsQuery,
} from '../../services/entitlements/entitlements.api';
import { entitlementErrorCopy } from '../../utils/billing-error/billing-error.util';
import { promoReplacementFor } from '../../utils/promo-replacement/promo-replacement.util';
import { useProductLabels } from '../../hooks/useProductLabels/useProductLabels';

export interface UseEntitlementGrantModalLogicInput {
  userId: string;
  onDone: () => void;
}

export interface PromoGrantValues {
  productKey: string;
  /** Three states — see `PromoDurationField`. Absent means "the plan decides". */
  durationDays?: number | null;
  note: string;
  /** Present only when this grant REPLACES one. Never sent. */
  confirmPhrase?: string;
}

export interface UseEntitlementGrantModalLogic {
  title: string;
  description: string;
  submitLabel: string;
  /** The label a replacement's submit wears, exposed so a spec need not pin copy. */
  replaceLabel: string;
  /** "Never expires", for the sentence and for the card that has no date. */
  neverExpiresLabel: string;
  fields: FieldDef[];
  schema: z.ZodType<PromoGrantValues, PromoGrantValues>;
  initialValues: PromoGrantValues;
  /** Whether this grant will OVERWRITE a live comp rather than add one. */
  isReplacement: boolean;
  /** What is about to be destroyed, translated, or `null`. */
  replacementWarning: string | null;
  /** What happens to the note and the granter on a replacement. `''` when none. */
  noteFateWarning: string;
  /** The address the operator must type back, or `null`. */
  confirmPhrase: string | null;
  /**
   * Who is being comped, for the dialog to name.
   *
   * The address when it can be read, the raw id otherwise — which is still what
   * an operator can search for and talk about, and is a great deal better than a
   * dialog that grants a subscription to nobody in particular.
   */
  memberLabel: string;
  /** Withholds the form until the member's rows are known — RHF reads defaults once. */
  isLoading: boolean;
  submit: (values: PromoGrantValues) => Promise<void>;
}

/**
 * Comping an account — and, when the account already holds a live comp,
 * **replacing** one.
 *
 * ## The whole reason this dialog is careful
 *
 * `UserEntitlementRepository.grantOrExtend` does not refuse a second grant. Under
 * an advisory lock it finds the live row for `(userId, 'promo')` and `UPDATE`s
 * `productKey`, `tier` and `currentPeriodEnd` **over it**. So a thirty-day
 * monthly comp granted to an App Review account holding an open-ended annual one
 * destroys the open-ended period, silently, with no undo, and answers `200`.
 *
 * `EntitlementAlreadyActiveError` reads exactly like the guard against that and
 * is **thrown nowhere** — there is no claim to refuse, so there is no refusal to
 * catch. The only place this can be caught is before the write, from the rows
 * this dialog reads itself.
 *
 * ## It re-reads rather than being handed the rows
 *
 * `ModalData` is serializable-only, so what travels is the member's id — and that
 * is the safer design anyway: a row list smuggled through the store would be a
 * snapshot a colleague may have changed since the page rendered, and *this*
 * snapshot decides whether the operator is warned at all. RTK dedupes the read
 * against the page's own subscription, so it costs nothing.
 *
 * ## Two warnings, because the api destroys two different things
 *
 * The first names the plan, tier and period being overwritten. The second is
 * subtler and is not in any plan: the `.set({…})` writes
 * `status · productKey · tier · currentPeriodEnd · externalRef · updatedAt` and
 * **not** `note` or `grantedByUserId`. The required justification the operator is
 * typing therefore never reaches the row — it goes only to the audit trail, while
 * the row keeps the previous grant's note and granter. An operator who is not
 * told that will believe they have replaced the record's explanation.
 *
 * ## Refusals go on the CONTROL, not into a banner
 *
 * Unlike the revoke dialog, nothing here is a race: the two refusals a grant can
 * meet are "that plan is gone" and something unmapped. The first is about what
 * the operator *chose*, so it is thrown with `details.productKey` and lands on
 * the picker they have to change (P21b — a refusal the api cannot place is placed
 * by the client). `ProductNotFoundError` carries no details of its own.
 */
export function useEntitlementGrantModalLogic({
  userId,
  onDone,
}: UseEntitlementGrantModalLogicInput): UseEntitlementGrantModalLogic {
  const { t } = useTranslation('payments');
  const { locale } = useDirection();
  const { formats } = useBrand();
  const { can } = useHasPermission();
  const toast = useToast();

  const query = useMemberEntitlementsQuery(userId, { skip: userId === '' });
  const [grantPromo] = useGrantPromoMutation();

  const rows = useMemo(() => query.data?.entitlements ?? [], [query.data]);
  const replacement = promoReplacementFor(rows);

  // Not an empty request — NO request. Asking for an id the operator may not
  // resolve is a 403 for a convenience that has a working absence (P21b).
  const memberIds = useMemo(
    () => (can('members.read') && userId !== '' ? [userId] : []),
    [can, userId],
  );
  const { items: memberOptions } = useResolveUserIds(memberIds);
  const memberEmail = memberOptions[0]?.label ?? null;

  const replacedKeys = useMemo(
    () => (replacement === null ? [] : [replacement.productKey]),
    [replacement],
  );
  const { labelOf } = useProductLabels(replacedKeys);

  const isReplacement = replacement !== null;
  // Dropped when the address cannot be read: demanding a phrase nobody can supply
  // is a dialog that can never be confirmed (P22b4).
  const confirmPhrase = isReplacement ? memberEmail : null;

  const neverExpiresLabel = t('entitlements.neverExpires');

  const schema = useMemo(() => buildSchema(confirmPhrase), [confirmPhrase]);

  const fields = useMemo(() => buildPromoGrantFields(t, { confirmPhrase }), [t, confirmPhrase]);

  const submit = useCallback(
    async (values: PromoGrantValues) => {
      try {
        await grantPromo(toRequest(userId, values)).unwrap();
        toast.success(
          t(isReplacement ? 'entitlements.done.replaced' : 'entitlements.done.granted'),
        );
        onDone();
      } catch (reason) {
        throw placeRefusal(toApiError(reason), t);
      }
    },
    [grantPromo, userId, toast, t, isReplacement, onDone],
  );

  return {
    title: t(isReplacement ? 'entitlements.grant.replaceTitle' : 'entitlements.grant.title'),
    description: t(
      isReplacement ? 'entitlements.grant.replaceSubtitle' : 'entitlements.grant.subtitle',
    ),
    submitLabel: t(
      isReplacement ? 'entitlements.grant.replaceConfirm' : 'entitlements.grant.confirm',
    ),
    replaceLabel: t('entitlements.grant.replaceConfirm'),
    neverExpiresLabel,
    fields,
    schema,
    // `durationDays` is deliberately absent rather than `undefined`: the form
    // opens on the plan's own duration, which is the one answer that commits the
    // platform to nothing it has not already decided.
    initialValues: { productKey: '', note: '' },
    isReplacement,
    replacementWarning:
      replacement === null
        ? null
        : t('entitlements.grant.replace.body', {
            plan: labelOf(replacement.productKey),
            tier: replacement.tier,
            ends:
              replacement.currentPeriodEnd === null
                ? neverExpiresLabel
                : formatDateTime(replacement.currentPeriodEnd, locale, formats),
          }),
    noteFateWarning: noteFate(replacement, t),
    confirmPhrase,
    memberLabel: memberEmail ?? userId,
    // Withheld until the rows arrive: RHF reads `defaultValues` ONCE, so a dialog
    // mounted before them would open as a plain grant and stay one even after the
    // live comp landed — which is the silent overwrite itself, one layer up.
    isLoading: query.isLoading,
    submit,
  };
}

/**
 * The form's shape, composed from the contract rather than restated (U3).
 *
 * `userId` comes from the screen and `confirmPhrase` is a control the api has
 * never heard of, so neither can be the contract's job. Composing keeps the two
 * in step: a field added to `PromoGrantCreateSchema` and not to the form becomes
 * a submit the api refuses, which `entitlements.api.spec` and this file's own
 * "what it sends" case both catch.
 */
function buildSchema(confirmPhrase: string | null): z.ZodType<PromoGrantValues, PromoGrantValues> {
  const base = PromoGrantCreateSchema.omit({ userId: true });

  if (confirmPhrase === null) {
    return base as z.ZodType<PromoGrantValues, PromoGrantValues>;
  }

  return base.extend({
    // Trimmed and case-SENSITIVE, matching `confirmStore`'s own comparison: the
    // phrase is an address with one spelling, and accepting a near-miss would
    // undo the point of asking for it.
    confirmPhrase: z.string().refine((typed) => typed.trim() === confirmPhrase.trim()),
  }) as z.ZodType<PromoGrantValues, PromoGrantValues>;
}

/**
 * The request, built key by key.
 *
 * **`durationDays` is set only when the form carries a value**, because the
 * service reads `'durationDays' in input`: an absent key asks for the plan's own
 * duration and an explicit `null` asks for a comp with no end. Spreading the
 * form's values instead would carry `confirmPhrase` into a `.strict()` body and
 * would leave the distinction to whatever the form happened to hold.
 */
function toRequest(userId: string, values: PromoGrantValues): PromoGrantCreateRequest {
  const request: PromoGrantCreateRequest = {
    userId,
    productKey: values.productKey,
    note: values.note,
  };

  return 'durationDays' in values && values.durationDays !== undefined
    ? { ...request, durationDays: values.durationDays }
    : request;
}

/**
 * Puts a refusal on the control that earned it, when the api could not.
 *
 * `ProductNotFoundError` carries no `details`, so `mapServerErrors` would print
 * its sentence above the form — beside nothing the operator can change. Anything
 * unmapped keeps the api's own message rather than a translation guessed at here.
 */
function placeRefusal(
  error: ReturnType<typeof toApiError>,
  t: (key: string) => string,
): ReturnType<typeof toApiError> {
  const copy = entitlementErrorCopy(error);

  if (copy?.field !== 'productKey') {
    return error;
  }

  return { ...error, details: { ...error.details, productKey: [t(copy.i18nKey)] } };
}

/** What a replacement does to the note and the granter — see the hook's docblock. */
function noteFate(
  replacement: UserEntitlement | null,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (replacement === null) {
    return '';
  }

  return replacement.note === null
    ? t('entitlements.grant.replace.noteFateEmpty')
    : t('entitlements.grant.replace.noteFate', { note: replacement.note });
}
